import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CalendarCheck, Mail, Puzzle } from "lucide-react";
import { scrollToId, IMAGES } from "@/components/Shared";

const lineAnim = (i) => ({
  initial: { y: "115%" },
  animate: { y: 0 },
  transition: { duration: 1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
});

const Hero = () => {
  const { scrollY } = useScroll();
  const yMain = useTransform(scrollY, [0, 700], [0, -70]);
  const yCard = useTransform(scrollY, [0, 700], [0, 50]);
  const yBadge = useTransform(scrollY, [0, 700], [0, -110]);

  return (
    <section id="hero" data-testid="hero-section" className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-24">
      <div className="pointer-events-none absolute -top-32 -right-40 h-[34rem] w-[34rem] rounded-full bg-amber-300/25 blur-3xl" />
      <div className="pointer-events-none absolute top-64 -left-48 h-[30rem] w-[30rem] rounded-full bg-deepteal/10 blur-3xl" />
      <Puzzle
        className="pointer-events-none absolute right-[8%] top-24 h-24 w-24 text-terracotta/10 rotate-12 hidden md:block"
        strokeWidth={1}
      />

      <div className="relative mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mb-6 flex flex-wrap items-center gap-2.5"
          >
            <span
              data-testid="hero-badge-open7days"
              className="inline-flex items-center gap-2 rounded-full border border-emerald-700/25 bg-emerald-50 px-4 py-1.5 font-mono text-[11px] font-semibold tracking-widest text-emerald-800 uppercase"
            >
              <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse-dot" />
              Open 7 Days a Week
            </span>
            <span
              data-testid="hero-badge-scholarships"
              className="inline-flex items-center gap-2 rounded-full border border-amber-700/25 bg-amber-100/80 px-4 py-1.5 font-mono text-[11px] font-semibold tracking-widest text-amber-900 uppercase"
            >
              Child Care Scholarships Accepted
            </span>
          </motion.div>

          <h1
            data-testid="hero-title"
            className="font-serif text-4xl sm:text-5xl lg:text-[4.4rem] font-medium tracking-tight leading-[1.04] text-deepteal-dark"
          >
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" {...lineAnim(0)}>
                Every child is an
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" {...lineAnim(1)}>
                important <em className="not-italic text-terracotta italic font-semibold">piece</em>
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span className="block" {...lineAnim(2)}>
                of our puzzle<span className="text-amber-600">.</span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-slate-600"
          >
            Safe, nurturing, dependable childcare for children ages 2–12 in Halethorpe, Maryland —
            from a licensed Maryland childcare provider who treats your family like our own.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <button
              data-testid="hero-cta-enroll"
              onClick={() => scrollToId("#enrollment")}
              className="group inline-flex items-center gap-2 rounded-full bg-terracotta px-7 py-3.5 text-sm font-bold text-amber-50 shadow-xl shadow-terracotta/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-deepteal"
            >
              Enroll Your Child
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-tour"
              onClick={() => scrollToId("#contact")}
              className="inline-flex items-center gap-2 rounded-full bg-deepteal px-7 py-3.5 text-sm font-bold text-amber-50 shadow-xl shadow-deepteal/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-deepteal-dark"
            >
              <CalendarCheck className="h-4 w-4" />
              Schedule a Tour
            </button>
            <button
              data-testid="hero-cta-contact"
              onClick={() => scrollToId("#contact")}
              className="inline-flex items-center gap-2 rounded-full border-2 border-deepteal/20 bg-white/70 px-7 py-3.5 text-sm font-bold text-deepteal backdrop-blur transition-all duration-300 hover:-translate-y-0.5 hover:border-terracotta hover:text-terracotta"
            >
              <Mail className="h-4 w-4" />
              Contact Us
            </button>
          </motion.div>

          <motion.div
            data-testid="hero-hours-strip"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-9 inline-flex flex-wrap items-center gap-x-4 gap-y-1 rounded-2xl border border-amber-900/10 bg-white/60 px-5 py-3.5 backdrop-blur"
          >
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-800">
              Monday–Sunday
            </span>
            <span className="hidden sm:block h-4 w-px bg-amber-900/20" />
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-deepteal">
              6:00 AM – 6:00 PM
            </span>
          </motion.div>
        </div>

        <div className="relative mx-auto w-full max-w-md lg:max-w-none">
          <motion.div
            style={{ y: yMain }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10"
          >
            <div className="arch-frame overflow-hidden border-[6px] border-white shadow-2xl shadow-deepteal/25">
              <img
                src={IMAGES.hero}
                alt="Black and white children learning and creating together with their teacher at Pieces to Our Puzzle Childcare in Halethorpe, MD"
                className="h-[420px] sm:h-[520px] w-full object-cover"
                loading="eager"
              />
            </div>
          </motion.div>

          <motion.div
            style={{ y: yCard }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="absolute -bottom-10 -left-6 sm:-left-12 z-20 w-40 sm:w-52 overflow-hidden rounded-2xl border-4 border-white shadow-xl shadow-deepteal/20 rotate-[-4deg]"
          >
            <img
              src={IMAGES.group}
              alt="Black and white children drawing together with their teacher at daycare in Halethorpe Maryland"
              className="h-32 sm:h-40 w-full object-cover"
            />
          </motion.div>

          <motion.div
            style={{ y: yBadge }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="absolute -top-8 -right-4 sm:-right-8 z-20"
          >
            <div className="relative flex h-28 w-28 sm:h-32 sm:w-32 items-center justify-center rounded-full bg-parchment shadow-xl shadow-amber-900/15 border border-amber-900/10">
              <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full animate-spin-slow">
                <defs>
                  <path id="circPath" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" />
                </defs>
                <text style={{ fontSize: "8.4px", letterSpacing: "2.1px" }} className="fill-deepteal font-mono font-semibold uppercase">
                  <textPath href="#circPath">Open 7 days · 6AM–6PM · Ages 2–12 ·</textPath>
                </text>
              </svg>
              <Puzzle className="h-8 w-8 text-terracotta" strokeWidth={1.6} />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
